import { CustomSankeyLayerProps, Sankey } from "@nivo/sankey";
import { useLayoutEffect, useRef, useState } from "react";
import { RyeIcon } from "../rye/RyeIcon";
import { buildClasses } from "../utils/buildClasses";
import { MockAppFrame, MockPanel, MockSelect } from "./MockAppFrame";

// A hand-built facsimile of the money flow report, for the marketing page.
//
// Unlike TransactionsMock, this one renders through the same charting library
// the real screen uses (@nivo/sankey), because the app already bundles it and
// hand-drawing the bands would only produce a worse copy of the same output.
// What is fabricated is the data, not the chart.
//
// The screen around it is Cash Flow's, copied from the real app. If you change
// it, change these:
//   sidebar   its own panel, w-[14.75rem], 8px from the content's
//             (CashFlowScreen, RyeMainContentSidebar wide); entries p-2
//             rounded-md gap-3, 1.2rem icon, text-sm, selected text-purple on
//             bg-on-surface-200 (ryeSidebarNavItemClasses), CASH_FLOW_VIEWS
//   top bars  h-11, px-[18px], title text-base on-surface-700 (RyeMainContentHeader)
//   controls  date preset RyeDropDown sm/w-44, then "Show unreviewed" and a
//             RyeToggle sm at the right (MoneyFlowReport)
//   card      18px padding around the chart (RyeMainContentCard)
//
// Chart settings below are copied from MoneyFlowReport. If you change the app,
// change these to match:
//   nodeThickness 16, nodeSpacing 18, nodeBorderRadius 3, nodeBorderWidth 0
//   linkOpacity 0.35, enableLinkGradient, labelPosition "outside"
//   labelOrientation "horizontal", labelPadding 12
//
// All figures here are fabricated. Never paste real financial data into this
// file.

// Copied from useMoneyFlowData -- nivo needs real colors, not class names.
const INCOME_COLOR = "#3FD088"; // green-500
const TOTAL_INCOME_COLOR = "#2AAC6B"; // green-600
const TOTAL_FLOW_COLOR = "#574b73"; // muted lavender, no Tailwind token
const EXPENSE_COLOR = "#0ea5e9"; // blue-500 (sky-500)
const TOTAL_EXPENSES_COLOR = "#0284c7"; // blue-600 (sky-600)
const SAVINGS_COLOR = "#7d57ff"; // purple-500
const TOTAL_SAVINGS_COLOR = "#6930f7"; // purple-600

const LABEL_COLOR = "#334155"; // on-surface-700
const LABEL_FONT_SIZE_PX = 12;
const LABEL_PADDING_PX = 12;
const LINK_OPACITY = 0.35;
const NODE_BORDER_RADIUS_PX = 3;

const TOTAL_FLOW_NODE_ID = "__total_flow__";
const TOTAL_INCOME_NODE_ID = "__total_income__";
const TOTAL_EXPENSES_NODE_ID = "__total_expenses__";
const TOTAL_SAVINGS_NODE_ID = "__total_savings__";

const CHART_THEME = {
  text: {
    fontFamily: "InterVariable, sans-serif",
    fontSize: LABEL_FONT_SIZE_PX,
    fill: LABEL_COLOR,
  },
};

type MockNode = {
  id: string;
  nodeLabel: string;
  nodeColor: string;
};

type MockLink = {
  source: string;
  target: string;
  value: number;
};

// A deliberately ordinary household, sketched over the eight months the header
// claims: one salary plus a bonus, a retirement contribution that arrives as
// income and leaves as savings, rent as the single largest line, and a long
// tail of smaller categories. The tail is kept short on purpose: this column
// has to fit fifteen-odd nodes inside a 16:9 card, and past about a dozen
// d3-sankey runs out of room and starts overlapping them.
//
// Most category names and emoji are the ones already visible in
// TransactionsMock, so a visitor who scrolls from the table to the chart sees
// the same vocabulary in both. Rent, Bonus, Misc. Income, 401k and Other
// Expenses exist only here -- the table shows a slice of one month, and these
// are what a full period adds.
//
// Array order is meaningful: it is what SORT_NODES below feeds to d3, which
// fixes the vertical order within each column. Categories descend by size, and
// expenses are listed before savings so the two groups never interleave.
//
// All figures are invented. Never paste real financial data into this file.
const NODES: MockNode[] = [
  { id: "paycheck", nodeLabel: "💰 Paycheck", nodeColor: INCOME_COLOR },
  { id: "bonus", nodeLabel: "🤑 Bonus", nodeColor: INCOME_COLOR },
  { id: "retirement_in", nodeLabel: "🏦 401k", nodeColor: INCOME_COLOR },
  { id: "misc_income", nodeLabel: "💵 Misc. Income", nodeColor: INCOME_COLOR },
  {
    id: TOTAL_INCOME_NODE_ID,
    nodeLabel: "Total Income",
    nodeColor: TOTAL_INCOME_COLOR,
  },
  {
    id: TOTAL_FLOW_NODE_ID,
    nodeLabel: "Total Flow",
    nodeColor: TOTAL_FLOW_COLOR,
  },
  {
    id: TOTAL_EXPENSES_NODE_ID,
    nodeLabel: "Total Expenses",
    nodeColor: TOTAL_EXPENSES_COLOR,
  },
  {
    id: TOTAL_SAVINGS_NODE_ID,
    nodeLabel: "Total Savings",
    nodeColor: TOTAL_SAVINGS_COLOR,
  },
  { id: "rent", nodeLabel: "🏠 Rent", nodeColor: EXPENSE_COLOR },
  { id: "groceries", nodeLabel: "🥑 Groceries", nodeColor: EXPENSE_COLOR },
  { id: "trips", nodeLabel: "🌴 Trips", nodeColor: EXPENSE_COLOR },
  { id: "restaurants", nodeLabel: "🌮 Restaurants", nodeColor: EXPENSE_COLOR },
  { id: "other", nodeLabel: "🧺 Other Expenses", nodeColor: EXPENSE_COLOR },
  { id: "fun", nodeLabel: "🎉 Fun", nodeColor: EXPENSE_COLOR },
  { id: "transport", nodeLabel: "🚗 Transport", nodeColor: EXPENSE_COLOR },
  { id: "gas", nodeLabel: "⛽ Gas", nodeColor: EXPENSE_COLOR },
  { id: "gifts", nodeLabel: "🎁 Gifts", nodeColor: EXPENSE_COLOR },
  {
    id: "retirement_out",
    nodeLabel: "📈 401k Savings",
    nodeColor: SAVINGS_COLOR,
  },
  { id: "new_car", nodeLabel: "🚙 New Car", nodeColor: SAVINGS_COLOR },
  { id: "brokerage", nodeLabel: "📊 Brokerage", nodeColor: SAVINGS_COLOR },
];

// Inflow and outflow balance at the waist by construction, exactly as the real
// report's data does. Income 63,200 = expenses 47,600 + savings 15,600, which
// puts savings at just under a quarter of everything going out.
const LINKS: MockLink[] = [
  { source: "paycheck", target: TOTAL_INCOME_NODE_ID, value: 48000 },
  { source: "bonus", target: TOTAL_INCOME_NODE_ID, value: 6000 },
  { source: "retirement_in", target: TOTAL_INCOME_NODE_ID, value: 7800 },
  { source: "misc_income", target: TOTAL_INCOME_NODE_ID, value: 1400 },
  { source: TOTAL_INCOME_NODE_ID, target: TOTAL_FLOW_NODE_ID, value: 63200 },
  { source: TOTAL_FLOW_NODE_ID, target: TOTAL_EXPENSES_NODE_ID, value: 47600 },
  { source: TOTAL_FLOW_NODE_ID, target: TOTAL_SAVINGS_NODE_ID, value: 15600 },
  { source: TOTAL_EXPENSES_NODE_ID, target: "rent", value: 19200 },
  { source: TOTAL_EXPENSES_NODE_ID, target: "groceries", value: 7200 },
  { source: TOTAL_EXPENSES_NODE_ID, target: "trips", value: 5300 },
  { source: TOTAL_EXPENSES_NODE_ID, target: "restaurants", value: 4500 },
  { source: TOTAL_EXPENSES_NODE_ID, target: "other", value: 4100 },
  { source: TOTAL_EXPENSES_NODE_ID, target: "fun", value: 2400 },
  { source: TOTAL_EXPENSES_NODE_ID, target: "transport", value: 2200 },
  { source: TOTAL_EXPENSES_NODE_ID, target: "gas", value: 1600 },
  { source: TOTAL_EXPENSES_NODE_ID, target: "gifts", value: 1100 },
  // The retirement contribution that came in as income leaves as savings, so
  // the two 401k lines are the same number on purpose.
  { source: TOTAL_SAVINGS_NODE_ID, target: "retirement_out", value: 7800 },
  { source: TOTAL_SAVINGS_NODE_ID, target: "new_car", value: 4200 },
  { source: TOTAL_SAVINGS_NODE_ID, target: "brokerage", value: 3600 },
];

// Without a comparator, d3-sankey orders each column by where the nodes at the
// other end sit, which let a small expense category sink below the savings
// branch. Passing one pins the order to the array above instead. Copied from
// MoneyFlowReport, which does the same thing for the same reason.
const NODE_ORDER = new Map(NODES.map((node, index) => [node.id, index]));

function sortNodes(a: MockNode, b: MockNode): number {
  return (NODE_ORDER.get(a.id) ?? 0) - (NODE_ORDER.get(b.id) ?? 0);
}

// The waist carries one label on each side: everything left of it is inflow,
// everything right of it is outflow. Nivo draws one label per node, so the pair
// is a layer and the node's own label is suppressed.
function WaistLabels({
  nodes,
}: CustomSankeyLayerProps<MockNode, MockLink>): JSX.Element | null {
  const waist = nodes.find((node) => node.id === TOTAL_FLOW_NODE_ID);
  if (!waist) return null;

  const centerY = waist.y + waist.height / 2;
  const labelStyle = {
    fontFamily: CHART_THEME.text.fontFamily,
    fontSize: LABEL_FONT_SIZE_PX,
    fill: LABEL_COLOR,
    pointerEvents: "none" as const,
  };

  return (
    <>
      <text
        x={waist.x - LABEL_PADDING_PX}
        y={centerY}
        textAnchor="end"
        dominantBaseline="central"
        style={labelStyle}
      >
        Total Inflow
      </text>
      <text
        x={waist.x + waist.width + LABEL_PADDING_PX}
        y={centerY}
        textAnchor="start"
        dominantBaseline="central"
        style={labelStyle}
      >
        Total Outflow
      </text>
    </>
  );
}

// The size of an element's layout box, ignoring transforms.
//
// ResponsiveSankey can't be used here: its auto-sizer measures with
// getBoundingClientRect, which includes transforms. The feature tour draws the
// mock through a scale() while docked, so resizing the window then sized the
// chart to the shrunken box, and nothing re-measured it when the scale went
// away.
function useLayoutSize(
  ref: React.RefObject<HTMLElement>,
): { width: number; height: number } | null {
  const [size, setSize] = useState<{ width: number; height: number } | null>(
    null,
  );

  useLayoutEffect(() => {
    const element = ref.current;
    if (element === null) return;

    // clientWidth and clientHeight are layout sizes, so transforms don't
    // affect them. Measured once up front as well, so the chart draws on the
    // first paint instead of waiting for the observer's first callback.
    const measure = () => {
      const width = element.clientWidth;
      const height = element.clientHeight;
      setSize((previous) =>
        previous !== null &&
        previous.width === width &&
        previous.height === height
          ? previous
          : { width, height },
      );
    };

    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(element);
    return () => observer.disconnect();
  }, [ref]);

  return size;
}

// Just the chart, which fills its parent.
function MoneyFlowChart(): JSX.Element {
  const containerRef = useRef<HTMLDivElement>(null);
  const size = useLayoutSize(containerRef);

  return (
    // The chart is absolutely positioned so its own size can never feed back
    // into the size being measured.
    <div
      ref={containerRef}
      className={buildClasses("relative", "h-full", "w-full")}
    >
      {size !== null && size.width > 0 && size.height > 0 && (
        <div className={buildClasses("absolute", "inset-0")}>
          <Sankey<MockNode, MockLink>
            width={size.width}
            height={size.height}
            data={{ nodes: NODES, links: LINKS }}
            // Side margins hold the outside labels.
            margin={{ top: 12, right: 172, bottom: 12, left: 118 }}
            align="justify"
            sort={sortNodes}
            colors={(node) => node.nodeColor}
            theme={CHART_THEME}
            nodeThickness={16}
            nodeSpacing={18}
            nodeBorderWidth={0}
            nodeBorderRadius={NODE_BORDER_RADIUS_PX}
            linkOpacity={LINK_OPACITY}
            linkBlendMode="normal"
            enableLinkGradient={true}
            isInteractive={false}
            animate={false}
            layers={["links", "nodes", "labels", WaistLabels]}
            label={(node) =>
              // The waist is labelled on both sides by WaistLabels.
              node.id === TOTAL_FLOW_NODE_ID ? "" : node.nodeLabel
            }
            labelPosition="outside"
            labelOrientation="horizontal"
            labelPadding={LABEL_PADDING_PX}
            labelTextColor={LABEL_COLOR}
          />
        </div>
      )}
    </div>
  );
}

// CASH_FLOW_VIEWS, in order.
const CASH_FLOW_VIEWS: { icon: string; label: string }[] = [
  { icon: "account_tree", label: "Sankey flow" },
  { icon: "align_horizontal_left", label: "Category breakdown" },
  { icon: "monitoring", label: "Historical trends" },
  { icon: "storefront", label: "Top merchants" },
];

// RyeMainContentHeaderRow: a top bar's title and what follows it.
function TopBar(props: {
  title: string;
  children?: React.ReactNode;
}): JSX.Element {
  return (
    <div className={buildClasses("shrink-0", "px-[18px]")}>
      <div className={buildClasses("flex", "h-11", "items-center", "gap-6")}>
        <div
          className={buildClasses(
            "shrink-0",
            "whitespace-nowrap",
            "text-base",
            "text-on-surface-700",
          )}
        >
          {props.title}
        </div>
        {props.children}
      </div>
    </div>
  );
}

function CashFlowSidebar(): JSX.Element {
  return (
    <MockPanel widthClassName="w-[14.75rem]" className="flex-col">
      <TopBar title="Cash Flow" />
      <div
        className={buildClasses(
          "flex",
          "flex-col",
          "gap-1",
          "px-[18px]",
          "pt-[18px]",
        )}
      >
        {CASH_FLOW_VIEWS.map((view, index) => {
          const selected = index === 0;
          return (
            <div
              key={view.label}
              className={buildClasses(
                "flex",
                "items-center",
                "gap-3",
                "rounded-md",
                "p-2",
                {
                  if: selected,
                  then: "bg-on-surface-200 text-purple",
                  else: "text-on-surface-500",
                },
              )}
            >
              <RyeIcon
                name={view.icon}
                fill={selected}
                size="custom"
                className={buildClasses("text-[1.2rem]")}
              />
              <span className={buildClasses("whitespace-nowrap", "text-sm")}>
                {view.label}
              </span>
            </div>
          );
        })}
      </div>
    </MockPanel>
  );
}

// A RyeToggle at size sm, switched off.
function MockToggle(): JSX.Element {
  return (
    <div
      className={buildClasses(
        "flex",
        "w-9",
        "rounded-full",
        "bg-on-surface-300",
        "p-0.5",
      )}
    >
      <div
        className={buildClasses(
          "h-3.5",
          "w-3.5",
          "rounded-full",
          "bg-on-surface-50",
        )}
      />
    </div>
  );
}

export function MoneyFlowMock(): JSX.Element {
  return (
    <MockAppFrame activeTab="cash-flow">
      <div className={buildClasses("flex", "min-w-0", "flex-1", "gap-2")}>
        <CashFlowSidebar />
        <MockPanel className="flex-col">
          <TopBar title="Sankey flow">
            <div
              className={buildClasses(
                "flex",
                "min-w-0",
                "flex-1",
                "items-center",
                "gap-3",
              )}
            >
              <MockSelect widthClassName="w-44">Year to date</MockSelect>
              <div
                className={buildClasses(
                  "ml-auto",
                  "flex",
                  "shrink-0",
                  "items-center",
                  "gap-2",
                )}
              >
                <span
                  className={buildClasses("text-sm", "text-on-surface-600")}
                >
                  Show unreviewed
                </span>
                <MockToggle />
              </div>
            </div>
          </TopBar>
          <div className={buildClasses("min-h-0", "flex-1", "p-[18px]")}>
            <MoneyFlowChart />
          </div>
        </MockPanel>
      </div>
    </MockAppFrame>
  );
}
